import { useCallback } from 'react'

import {
  getAllAncestorsForItem,
  getAllDescendantsForItem,
  getDisplayValues,
  getLeaves,
  getOriginalData,
  getTreeItemState,
  IDataShape,
  ITreeStructure,
} from 'cng-common/components/treeSelect'
import { deDupeTreeItems } from 'cng-common/components/treeSelect/helpers/deDupeTreeItems'
import { getAncestorsToRemove } from 'cng-common/components/treeSelect/helpers/getAncestorsToRemove'
import { IUseTreeDataResult } from 'cng-common/components/treeSelect/models/iUseTreeData'
import {
  IUseTreeSelectParameters,
  IUseTreeSelectResult,
} from 'cng-common/components/treeSelect/models/iUseTreeSelect'
import { ITreeItemClickFn } from 'cng-common/components/treeSelect/models/treeSelectProps'

import { PREVENT_PARENT_AUTO_SELECT } from '../configs'

import { useTreeData } from './useTreeData'

export const useTreeSelect = <T extends IDataShape>({
  data,
  values,
  itemCaption,
  onChange,
  onSearch,
  typeOfSelectedNodes,
}: IUseTreeSelectParameters<T>): IUseTreeSelectResult &
  IUseTreeDataResult & {
    selectFromCompactParent: ITreeItemClickFn
    selectParentOnly: ITreeItemClickFn
  } => {
  const {
    treeData,
    flatData,
    searchValue,
    setSearchValue,
    showOnlySelected,
    setShowOnlySelected,
    selectedValues,
  } = useTreeData({
    data,
    values,
    itemCaption,
    onSearch,
    typeOfSelectedNodes,
  })

  const expandSelectedValues = useCallback(
    (compactSelected: ITreeStructure[]): ITreeStructure[] =>
      compactSelected.reduce<ITreeStructure[]>((acc, sv) => {
        if (sv.checkedState === 'full' && sv.hasChildren) {
          return [...acc, ...getAllDescendantsForItem(sv)]
        }

        return [...acc, sv]
      }, []),
    []
  )

  const handleChangeEvent = useCallback(
    (allSelectedItems: ITreeStructure[]) => {
      const displayedOriginalItems = getOriginalData<T>(getDisplayValues(allSelectedItems))
      const onlyLeavesItems = getOriginalData<T>(getLeaves(allSelectedItems))
      const allSelectedOriginalItems = getOriginalData<T>(allSelectedItems)

      onChange(displayedOriginalItems, allSelectedOriginalItems, onlyLeavesItems)
    },
    [onChange]
  )

  const removeAll = useCallback(() => {
    handleChangeEvent([])
  }, [handleChangeEvent])

  const removeItem = useCallback<ITreeItemClickFn>(
    (itemId) => {
      const item = flatData.find((it) => it.itemId === itemId)
      if (!item) {
        return
      }

      const allAncestors = getAllAncestorsForItem(item)
      const allDescendants = getAllDescendantsForItem(item).filter(
        (it) => it.itemId !== item.itemId
      )

      const isDirectlySelected = selectedValues.some((sv) => sv.itemId === itemId)
      if (!isDirectlySelected) {
        const compactAncestor = allAncestors.find((a) =>
          selectedValues.some((sv) => sv.itemId === a.itemId && sv.checkedState === 'full')
        )

        if (compactAncestor) {
          const remaining = selectedValues.filter((sv) => sv.itemId !== compactAncestor.itemId)
          const expandedRemaining = expandSelectedValues(remaining)
          const newState = expandedRemaining.map((it, i, origin) => ({
            ...it,
            checkedState: getTreeItemState(it.itemId, flatData, origin),
          }))

          handleChangeEvent(newState)
          return
        }
      }

      const expandedSelectedValues = expandSelectedValues(selectedValues)

      if (item.parentId === null) {
        const itemsToUncheck = [...allAncestors, item, ...allDescendants]
        const newState = expandedSelectedValues
          .filter((it) => !itemsToUncheck.some((uncheck) => uncheck.itemId === it.itemId))
          .map((it, i, origin) => ({
            ...it,
            checkedState: getTreeItemState(it.itemId, flatData, origin),
          }))

        handleChangeEvent(newState)
        return
      }

      let updatedState = expandedSelectedValues.filter((it) => item.itemId !== it.itemId)
      if (allDescendants.length) {
        updatedState = updatedState.filter((it) => {
          return !allDescendants.some((descendant) => descendant.itemId === it.itemId)
        })
      }

      const ancestorsToRemove = getAncestorsToRemove(item, updatedState)
      updatedState = updatedState
        .filter((it) => !ancestorsToRemove.some((ancestor) => ancestor.itemId === it.itemId))
        .map((it, i, origin) => ({
          ...it,
          checkedState: getTreeItemState(it.itemId, flatData, origin),
        }))

      handleChangeEvent(updatedState)
    },
    [flatData, selectedValues, expandSelectedValues, handleChangeEvent]
  )

  const selectItem = useCallback<ITreeItemClickFn>(
    (itemId) => {
      const item = flatData.find((it) => it.itemId === itemId)
      if (!item) {
        return
      }

      const allAncestors = PREVENT_PARENT_AUTO_SELECT ? [] : getAllAncestorsForItem(item)
      const allDescendants = getAllDescendantsForItem(item).filter(
        (it) => it.itemId !== item.itemId
      )

      const baseSelected = PREVENT_PARENT_AUTO_SELECT
        ? selectedValues.filter((sv) => sv.checkedState === 'full')
        : selectedValues

      const newState = [
        ...expandSelectedValues(baseSelected),
        ...allAncestors,
        item,
        ...allDescendants,
      ]
        .filter(deDupeTreeItems)
        .map((it, i, origin) => ({
          ...it,
          checkedState: getTreeItemState(it.itemId, flatData, origin),
        }))

      handleChangeEvent(newState)
    },
    [flatData, expandSelectedValues, selectedValues, handleChangeEvent]
  )

  const selectFromCompactParent = useCallback<ITreeItemClickFn>(
    (itemId) => {
      const child = flatData.find((it) => it.itemId === itemId)
      if (!child) return

      const ancestors = getAllAncestorsForItem(child)
      const withoutCompactAncestors = selectedValues.filter(
        (sv) => !ancestors.some((a) => a.itemId === sv.itemId && sv.checkedState === 'full')
      )
      const expandedRemaining = expandSelectedValues(withoutCompactAncestors)

      const newState = [...expandedRemaining, child]
        .filter(deDupeTreeItems)
        .map((it, i, origin) => ({
          ...it,
          checkedState: getTreeItemState(it.itemId, flatData, origin),
        }))

      handleChangeEvent(newState)
    },
    [selectedValues, flatData, expandSelectedValues, handleChangeEvent]
  )

  const selectParentOnly = useCallback<ITreeItemClickFn>(
    (itemId) => {
      const parent = flatData.find((it) => it.itemId === itemId)
      if (!parent) return

      const allDescendantIds = new Set(
        getAllDescendantsForItem(parent)
          .filter((it) => it.itemId !== parent.itemId)
          .map((it) => it.itemId)
      )

      const withoutDescendants = selectedValues.filter(
        (sv) =>
          sv.checkedState === 'full' &&
          !allDescendantIds.has(sv.itemId) &&
          sv.itemId !== parent.itemId
      )

      const expandedRemaining = expandSelectedValues(withoutDescendants)
      const compactParent: ITreeStructure = {
        ...parent,
        checkedState: 'full',
        hasChildren: false,
      }

      const newState = [...expandedRemaining, compactParent]
        .filter(deDupeTreeItems)
        .map((it, i, origin) => {
          if (it.itemId === parent.itemId) return it

          return {
            ...it,
            checkedState: getTreeItemState(it.itemId, flatData, origin),
          }
        })

      handleChangeEvent(newState)
    },
    [selectedValues, flatData, expandSelectedValues, handleChangeEvent]
  )

  return {
    removeItem,
    removeAll,
    selectItem,
    selectFromCompactParent,
    selectParentOnly,

    treeData,
    flatData,
    searchValue,
    setSearchValue,
    showOnlySelected,
    setShowOnlySelected,
    selectedValues,
  }
}
